import React from 'react';
import type { Table } from '@tanstack/react-table';
import type { ColumnMetadata } from '../../types';
import { CustomDatePicker } from '../../../../components/common/CustomDatePicker';
import {
  formatDateTimeForInput,
  isBooleanValueTrue,
  toBackendDateString,
  toBackendDateTimeString,
  toBooleanStorageValue,
} from '../../utils';
import CustomDropdown from '../../../../components/common/CustomDropdown';
import { getEndOfToday } from '../../../../components/common/datePickerMaxDate';
import './editableCell.css';

type EditableCellProps = {
  initialValue: unknown;
  rowIndex: number;
  columnId: string;
  columnMetadata?: ColumnMetadata;
  table: Table<Record<string, unknown>>;
  rowId?: string;
  rowData?: Record<string, unknown>;
};

const getWorkLogOwnerRelationId = (rowData?: Record<string, unknown>): number | null => {
  if (!rowData) return null;

  const value =
    rowData.CrmRelationId ?? rowData.crmRelationId ?? rowData.AssignedId ?? rowData.assignedId;
  const relationId = Number(value);

  return Number.isFinite(relationId) && relationId > 0 ? relationId : null;
};

const EditableCellComponent = ({
  initialValue,
  rowIndex,
  columnId,
  columnMetadata,
  table,
  rowId,
  rowData,
}: EditableCellProps) => {
  'use memo';
  // Primary detection from metadata
  let fieldType: 'date' | 'datetime' | 'other' = 'other';

  if (columnMetadata?.type === 'datetime' || columnMetadata?.dataType === 'datetime') {
    fieldType = 'datetime';
  } else if (columnMetadata?.type === 'date' || columnMetadata?.dataType === 'date') {
    fieldType = 'date';
  } else if (columnMetadata?.DataType === 'date') {
    // Fallback for case-sensitive issues
    fieldType = 'date';
  } else {
    // Fallback: detect from value format if metadata is missing
    const strValue = String(initialValue);
    if (strValue && strValue.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
      // Value looks like ISO datetime, but no type metadata
      // Default to datetime to preserve time information
      fieldType = 'datetime';
    }
  }

  const [value, setValue] = React.useState<Date | string | null>(
    formatDateTimeForInput(initialValue, fieldType, table.options.meta?.dateTimeMode)
  );

  React.useEffect(() => {
    setValue(formatDateTimeForInput(initialValue, fieldType, table.options.meta?.dateTimeMode));
  }, [initialValue, fieldType, table.options.meta?.dateTimeMode]);

  const commitToGrid = React.useCallback(
    (nextValue: unknown) => {
      table.options.meta?.updateData(rowIndex, columnId, nextValue);
    },
    [table, rowIndex, columnId]
  );

  const handleDateChange = (dateValue: Date | null) => {
    setValue(dateValue);

    if (!dateValue) {
      commitToGrid(null);
      return;
    }

    if (fieldType === 'date') {
      commitToGrid(toBackendDateString(dateValue));
      return;
    }

    if (fieldType === 'datetime') {
      commitToGrid(toBackendDateTimeString(dateValue, table.options.meta?.dateTimeMode));
    }
  };

  const onBlur = () => {
    // Date/datetime handled by handleDateChange, only handle other types
    if (fieldType !== 'date' && fieldType !== 'datetime') {
      commitToGrid(value);
    }
  };

  const updateLocalValue = (nextValue: string) => {
    setValue(nextValue);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.target.select();
  };

  const lookupMaps = table.options.meta?.lookupOptionMaps ?? table.options.meta?.lookupMaps;
  const displayLookupMaps = table.options.meta?.lookupMaps;
  const fieldName = columnMetadata?.dataField || columnMetadata?.field || columnId;
  const lookupMap = lookupMaps?.get(fieldName);
  const displayLookupMap = displayLookupMaps?.get(fieldName);
  const getSiblingDate = (): Date | null => {
    if (fieldType !== 'date' && fieldType !== 'datetime') return null;
    if (!columnMetadata?.pairedDateField) return null;

    const raw = table.getRowModel().rows[rowIndex]?.getValue(columnMetadata.pairedDateField);
    if (!raw) return null;

    const parsed = formatDateTimeForInput(raw, fieldType, table.options.meta?.dateTimeMode);
    return parsed instanceof Date ? parsed : null;
  };

  const baseStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    border: 'none',
    padding: '0',
    margin: '0',
    fontSize: '0.8125rem',
    fontFamily: 'inherit',
    fontVariantNumeric: 'normal',
    color: 'inherit',
    outline: 'none',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  if (lookupMap) {
    return (
      <div className="editable-cell-lookup">
        <CustomDropdown
          options={Object.entries(lookupMap).map(([id, label]) => ({ value: id, label }))}
          value={String(value ?? '') || undefined}
          selectedLabelOverride={displayLookupMap?.[String(value ?? '')]}
          onChange={(nextValue) => {
            setValue(nextValue ?? '');
            commitToGrid(nextValue ?? '');
          }}
          searchable
          showAllOption={false}
          placeholder=""
        />
      </div>
    );
  }

  if (fieldType === 'date' || fieldType === 'datetime') {
    const currentUserRelationId = table.options.meta?.currentUserRelationId;
    const ownerRelationId = getWorkLogOwnerRelationId(rowData);
    const canBypassHistoricalLock =
      table.options.meta?.canEditLockedWorkLogs === true &&
      Boolean(rowId) &&
      !rowId?.startsWith('temp_') &&
      rowId !== '0' &&
      ownerRelationId !== null &&
      typeof currentUserRelationId === 'number' &&
      currentUserRelationId > 0 &&
      ownerRelationId !== currentUserRelationId;
    const dateRestrictions =
      (columnId === 'LogStart' || columnId === 'LogEnd') && !canBypassHistoricalLock
        ? {
            userRelationDays: table.options.meta?.userRelationDays,
            userRelationVacationDays: table.options.meta?.userRelationVacationDays,
          }
        : undefined;
    return (
      <CustomDatePicker
        value={value as Date | null}
        defaultValue={getSiblingDate}
        onChange={handleDateChange}
        mode={fieldType}
        compact
        dateRestrictions={dateRestrictions}
        maxDate={table.options.meta?.dataOptionsMax === true ? getEndOfToday() : null}
      />
    );
  }

  if (columnMetadata?.type === 'number' || columnMetadata?.dataType === 'number') {
    return (
      <input
        type="number"
        value={String(value ?? '')}
        onChange={(e) => updateLocalValue(e.target.value)}
        onBlur={onBlur}
        onFocus={handleFocus}
        style={baseStyle}
      />
    );
  }

  if (columnMetadata?.type === 'boolean' || columnMetadata?.dataType === 'boolean') {
    const isChecked = isBooleanValueTrue(value);
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => {
            const next = toBooleanStorageValue(e.target.checked);
            setValue(next);
            commitToGrid(next);
          }}
          style={{
            width: '18px',
            height: '18px',
            cursor: 'pointer',
          }}
        />
      </div>
    );
  }

  if (columnMetadata?.editor === 'textarea') {
    return (
      <textarea
        value={String(value ?? '')}
        onChange={(e) => updateLocalValue(e.target.value)}
        onBlur={onBlur}
        onFocus={handleFocus}
        rows={4}
        style={{
          ...baseStyle,
          resize: 'vertical',
          whiteSpace: 'pre-wrap',
          overflow: 'auto',
          textOverflow: 'clip',
        }}
      />
    );
  }

  return (
    <input
      value={String(value ?? '')}
      onChange={(e) => updateLocalValue(e.target.value)}
      onBlur={onBlur}
      onFocus={handleFocus}
      style={baseStyle}
    />
  );
};

export const EditableCell = EditableCellComponent;
